import React from 'react';
import { motion } from 'framer-motion';
import { LucideIcon } from 'lucide-react';

interface EnhancedFeatureCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  color: string;
  index: number;
  className?: string;
  variant?: 'gradient' | 'glass' | 'minimal';
  showAnimation?: boolean;
  stepNumber?: number;
  markers?: string[];
}

/**
 * Enhanced feature card component with premium visual appeal
 * Matches the homepage's aesthetic with advanced styling options
 */
export const EnhancedFeatureCard: React.FC<EnhancedFeatureCardProps> = ({
  icon: Icon,
  title,
  description,
  color,
  index,
  className = '',
  variant = 'gradient',
  showAnimation = true,
  stepNumber,
  markers
}) => {
  const [hover, setHover] = React.useState(false);
  const getCardStyles = () => {
    let variantStyles = '';
    
    switch (variant) {
      case 'glass':
        variantStyles = 'bg-theme-surface/40 backdrop-blur-md border-theme-border/30 hover:bg-theme-surface/60';
        break;
      case 'gradient':
        variantStyles = 'bg-gradient-to-br from-theme-surface to-theme-elevated border-theme-border/50 hover:border-theme-primary/50';
        break;
      case 'minimal':
        variantStyles = 'bg-transparent border-theme-border/20 hover:border-theme-primary/30 hover:bg-theme-surface/30';
        break;
      default:
        variantStyles = 'bg-theme-surface border-theme-border';
    }

    const baseStyles = `group relative overflow-hidden rounded-3xl border transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-theme-primary/10 ${variantStyles}`;
    return baseStyles;
  };

  const getIconStyles = () => {
    const baseIconStyles = 'w-8 h-8 transition-all duration-300 group-hover:scale-110';
    
    const colorMappings: Record<string, string> = {
      'from-theme-primary to-theme-secondary': 'text-theme-primary',
      'blue': 'text-blue-500',
      'emerald': 'text-emerald-500',
      'amber': 'text-amber-500',
      'indigo': 'text-indigo-500',
      'purple': 'text-purple-500'
    };
    
    const iconColor = colorMappings[color] || 'text-theme-primary';
    return `${baseIconStyles} ${iconColor}`;
  };

  const getIconContainerStyles = () => {
    return 'w-16 h-16 rounded-2xl flex items-center justify-center mb-6 transition-all duration-500 bg-theme-background border border-theme-border shadow-inner group-hover:shadow-theme-primary/20 group-hover:bg-theme-surface';
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.8,
        delay: index * 0.1,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  return (
    <motion.div
      variants={cardVariants}
      initial={showAnimation ? "hidden" : "visible"}
      whileInView="visible"
      viewport={{ once: true, margin: "-50px" }}
      className={getCardStyles() + ' ' + className}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {/* Background Animated Glow */}
      <div className="absolute -inset-24 bg-theme-primary/5 blur-[100px] rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-1000 pointer-events-none" />

      {/* Content */}
      <div className="relative p-8 z-10 flex flex-col h-full">
        {/* Step Number */}
        {stepNumber && (
          <div className="absolute top-6 right-6 w-10 h-10 bg-theme-primary rounded-xl flex items-center justify-center text-theme-text-inverse text-lg font-black shadow-lg shadow-theme-primary/30 transform group-hover:rotate-12 transition-transform">
            {stepNumber}
          </div>
        )}

        {/* Icon Container */}
        <div className={getIconContainerStyles()}>
          <motion.div animate={hover ? { rotate: [0, -10, 10, 0] } : { rotate: 0 }} transition={{ duration: 0.5 }}>
            <Icon className={getIconStyles()} />
          </motion.div>
        </div>

        {/* Text Content */}
        <h3 className="text-2xl font-black text-theme-text-primary mb-3 leading-tight group-hover:text-theme-primary transition-colors duration-300">
          {title}
        </h3>

        <p className="text-theme-text-secondary font-medium leading-relaxed mb-6 group-hover:text-theme-text-primary transition-colors duration-300">
          {description}
        </p>

        {/* Markers */}
        {markers && markers.length > 0 && (
          <div className="mt-auto pt-6 border-t border-theme-border/30">
            <p className="text-[10px] font-black text-theme-text-muted uppercase tracking-[0.2em] mb-3">Core Markers</p>
            <div className="flex flex-wrap gap-2">
              {markers.map((marker, markerIndex) => (
                <span
                  key={markerIndex}
                  className="px-3 py-1 bg-theme-background/50 backdrop-blur-sm text-theme-primary text-[10px] rounded-full font-black border border-theme-border group-hover:border-theme-primary/30 transition-colors"
                >
                  {marker}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Bottom Accent Line */}
        <div className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-theme-primary to-theme-secondary w-0 group-hover:w-full transition-all duration-700" />
      </div>
    </motion.div>
  );
};

/**
 * Feature grid container with consistent spacing and layout
 */
export const FeatureGrid: React.FC<{
  children: React.ReactNode;
  columns?: 1 | 2 | 3 | 4;
  gap?: 'sm' | 'md' | 'lg';
  className?: string;
}> = ({ 
  children, 
  columns = 3, 
  gap = 'lg',
  className = '' 
}) => {
  const gridClasses = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
  };

  const gapClasses = {
    sm: 'gap-4',
    md: 'gap-6',
    lg: 'gap-8'
  };

  return (
    <div className={`grid ${gridClasses[columns]} ${gapClasses[gap]} ${className}`}>
      {children}
    </div>
  );
};

export default EnhancedFeatureCard;
